import { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, XAxis, YAxis } from 'recharts';
import { PanelCard } from '../../shared/PanelCard/PanelCard';
import classes from './HourlyHistogram.module.css';

interface IHourlyHistogramProps {
  histogram: Record<string, string[]> | null;
  isLoading: boolean;
  /**
   * When true, the section's card chrome (background + border-radius + shadow
   * + padding) and the "Hourly histogram" title are suppressed so the chart
   * can be nested inside a shared shell with <VisitsCard />. Default false.
   */
  flush?: boolean;
}

// 6 ticks across the 24-h axis with lowercase am/pm (matches trusstor's
// visual). Recharts won't render a tick beyond the dataset (24 bars at
// indices 00..23), so we stop at "20" rather than echoing the closing
// "12 am" — the bar gap to the right edge reads as the missing label.
const TICK_HOURS = ['00', '04', '08', '12', '16', '20'];
const TICK_LABELS: Record<string, string> = {
  '00': '12 am', '04': '4 am', '08': '8 am',
  '12': '12 pm', '16': '4 pm', '20': '8 pm',
};
const FALLBACK_WIDTH = 600;
const CHART_HEIGHT = 120;

function toBars(histogram: Record<string, string[]>): { hour: string; count: number }[] {
  const out: { hour: string; count: number }[] = [];
  for (let h = 0; h < 24; h++) {
    const key = h.toString().padStart(2, '0');
    out.push({ hour: key, count: (histogram[key] ?? []).length });
  }
  return out;
}

export function HourlyHistogram(props: IHourlyHistogramProps) {
  const { t } = useTranslation();
  const { histogram, isLoading, flush = false } = props;
  const title = t('reports.observeLocation.section.histogram');
  const containerRef = useRef<HTMLDivElement>(null);
  const [chartWidth, setChartWidth] = useState(FALLBACK_WIDTH);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => {
      const w = el.getBoundingClientRect().width;
      if (w > 0) setChartWidth(w);
    };
    measure();
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(measure);
      ro.observe(el);
      return () => ro.disconnect();
    }
    return undefined;
  }, []);

  if (isLoading || !histogram) {
    return (
      <PanelCard
        flush={flush}
        className={flush ? undefined : classes.sized}
        aria-busy="true"
        aria-label={title}
      >
        {!flush && <h4 className={classes.title}>{title}</h4>}
        <div className={classes.skeleton} />
      </PanelCard>
    );
  }

  const data = toBars(histogram);

  return (
    <PanelCard
      flush={flush}
      className={flush ? undefined : classes.sized}
      aria-label={title}
    >
      {!flush && <h4 className={classes.title}>{title}</h4>}
      <div className={classes.chart} ref={containerRef}>
        <BarChart
          width={chartWidth}
          height={CHART_HEIGHT}
          data={data}
          margin={{ top: 4, right: 4, bottom: 4, left: 4 }}
        >
          <XAxis
            dataKey="hour"
            ticks={TICK_HOURS}
            tickFormatter={(h: string) => TICK_LABELS[h] ?? h}
            tick={{ fontSize: 10 }}
            interval={0}
          />
          <YAxis hide />
          <Bar
            dataKey="count"
            /* Uses the `--bar-blue` design token (#3582ff) which matches
               trusstor's LocationHistogram `BAR_COLOR` exactly. Recharts
               accepts CSS custom-property values directly. d-orca's `--blue`
               token is `#2ebffd` (cyan) — a distinct token was added to
               avoid the saturation mismatch. */
            fill="var(--bar-blue)"
            radius={[2, 2, 0, 0]}
            isAnimationActive={false}
            minPointSize={1}
            barSize={14}
          />
        </BarChart>
        {/* Recharts won't render an axis tick past the dataset's last point.
            Trusstor's design closes the axis with a `12 am` label at the
            right edge — render it as a CSS overlay aligned with the chart
            margin so it sits directly beneath the right-edge bar. */}
        <span className={classes.trailingTick}>12 am</span>
      </div>
    </PanelCard>
  );
}
